import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, forwardRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ui-textarea',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiTextareaComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <textarea
      [id]="id"
      [rows]="rows"
      [placeholder]="placeholder"
      [value]="modelValue ?? ''"
      (input)="onInputEvent($event)"
      (change)="change.emit($event)"
      (blur)="onBlurEvent()"
      [attr.name]="name"
      [required]="required"
      [disabled]="disabled || disabledByForm"
      [readonly]="readonly"
      [attr.aria-label]="ariaLabel"
      [class]="textareaClass"
      [attr.maxlength]="maxlength"
      [attr.minlength]="minlength"
    ></textarea>
  `,
})
export class UiTextareaComponent implements ControlValueAccessor {
  @Input() id?: string;
  @Input() rows: number = 3;
  @Input() value?: string | null;
  @Input() placeholder?: string;
  @Input() name?: string;
  @Input() required = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() ariaLabel?: string;
  @Input() maxlength?: number;
  @Input() minlength?: number;
  @Input() textareaClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  @Input() formControl?: FormControl;
  @Input() ngModel?: any;
  @Output() ngModelChange = new EventEmitter<any>();
  @Output() input = new EventEmitter<Event>();
  @Output() change = new EventEmitter<Event>();

  modelValue: any = '';
  disabledByForm = false;
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {
    this.modelValue = value ?? '';
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm = isDisabled;
  }

  onInputEvent(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.modelValue = value;
    this.ngModelChange.emit(value);
    this.onChangeFn(value);
    this.input.emit(event);
  }

  onBlurEvent(): void {
    this.onTouchedFn();
  }
}
